import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

const peso = (v) => `₱${Number(v ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Sessions whose billing was suspended because of unusual meter readings. Customer Care either bills
 * the session from its last good reading or releases the driver's hold; both need a note.
 * Renders nothing when the queue is unavailable (older API) so the rest of the console still works.
 */
export default function AuditHolds({ onResolved }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null); // { sessionId, action }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => api.listAuditHolds().then((list) => setRows(Array.isArray(list) ? list : [])).catch(() => setRows(null)), []);
  useEffect(() => { load(); }, [load]);

  if (!rows) return null;

  const resolve = async () => {
    if (note.trim().length < 5) { setError('A note of at least 5 characters is required.'); return; }
    setBusy(true);
    setError('');
    try {
      await api.resolveAuditHold(open.sessionId, { action: open.action, note: note.trim() });
      onResolved?.(open.action === 'bill' ? 'Session billed from its last good reading.' : 'Hold released; the session was not billed.');
      setOpen(null);
      setNote('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card border shadow-sm bg-white mb-4" id="audit-holds-card">
      <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
        <h6 className="mb-0 fw-bold" style={{ color: '#005293' }}><i className="fas fa-pause-circle me-2" aria-hidden="true" />Audit holds</h6>
        <span className={`badge ${rows.length ? 'bg-warning text-dark' : 'bg-secondary'}`} id="audit-holds-count">{rows.length} waiting</span>
      </div>
      <div className="card-body p-3">
        <p className="text-muted small mb-2">Sessions with unusual meter readings are not billed automatically. Review each one, then bill it or release the driver&apos;s hold.</p>
        {rows.length === 0 && <div className="text-muted small">No sessions are waiting for review.</div>}
        {rows.map((r) => (
          <div key={r.sessionId} className="border rounded p-2 mb-2" data-session-id={r.sessionId} style={{ overflowWrap: 'anywhere' }}>
            <div className="d-flex flex-wrap justify-content-between gap-2">
              <div>
                <div className="fw-semibold">{r.sessionCode} <span className="text-muted fw-normal">· {r.siteName || r.chargePoint}</span></div>
                <div className="text-muted small">{r.driverName} ({r.driverEmail}) · stopped {r.stoppedAt ? new Date(r.stoppedAt).toLocaleString() : '—'}</div>
                <div className="small text-danger">{r.anomalyFlag}</div>
              </div>
              <div className="text-end small">
                <div>{Number(r.energyKwh ?? 0).toFixed(2)} kWh · est. <strong>{peso(r.estimatedTotal)}</strong></div>
                <div className="text-muted">Hold {peso(r.holdAmount)}</div>
              </div>
            </div>
            {open?.sessionId === r.sessionId ? (
              <div className="mt-2">
                <label className="form-label small fw-bold" htmlFor={`hold-note-${r.sessionId}`}>{open.action === 'bill' ? 'Bill this session' : 'Release the hold'} — note for the audit log</label>
                <textarea id={`hold-note-${r.sessionId}`} className="form-control mb-2" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
                {error && <div className="text-danger small mb-2" role="alert">{error}</div>}
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className={`btn btn-sm ${open.action === 'bill' ? 'btn-primary' : 'btn-warning'}`} disabled={busy} onClick={resolve}>{busy ? 'Saving…' : 'Confirm'}</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => { setOpen(null); setError(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2 mt-2">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setOpen({ sessionId: r.sessionId, action: 'bill' }); setNote(''); setError(''); }}>Bill from last good reading</button>
                <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => { setOpen({ sessionId: r.sessionId, action: 'release' }); setNote(''); setError(''); }}>Release hold</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
